// ══════════════════════════════════════════════════════════════════════════════
// PAGE VISIO PRO - Créer des réunions Google Meet rapidement
// AbracadaWorld - Cockpit Pro (Artisans/Vendeurs)
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video, Plus, Copy, ExternalLink, Clock, Users, Calendar,
  Check, X, Link2, Zap, Share2, Mail, ArrowLeft, Sparkles, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  return localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
};

export default function VisioProPage() {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  const [scheduledMeeting, setScheduledMeeting] = useState({
    summary: '',
    date: '',
    start_time: '09:00',
    duration: '30',
    attendees: ''
  });

  // Détection du clavier et masquage navbar
  useEffect(() => {
    if (!showScheduleModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showScheduleModal, isMobile]);

  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) loadRecentMeetings();
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  const connectGoogle = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const loadRecentMeetings = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
      
      const res = await fetch(
        `${API_URL}/api/google/calendar/events?time_min=${now.toISOString()}&time_max=${nextMonth.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        const meetingsWithMeet = (data.events || []).filter(e => e.meet_link);
        setRecentMeetings(meetingsWithMeet);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createInstantMeeting = async () => {
    setShowQuickCreate(true);
    
    try {
      const token = getToken();
      const now = new Date();
      const end = new Date(now.getTime() + 60 * 60 * 1000);
      
      const res = await fetch(`${API_URL}/api/google/calendar/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          summary: 'Visio Client',
          description: 'Créée depuis AbracadaWorld Pro',
          start_datetime: now.toISOString(),
          end_datetime: end.toISOString(),
          attendees: [],
          add_meet: true
        })
      });

      if (res.ok) {
        const data = await res.json();
        setCreatedMeeting(data.event);
        loadRecentMeetings();
      } else {
        toast.error('Erreur de création');
        setShowQuickCreate(false);
      }
    } catch (error) {
      toast.error('Erreur');
      setShowQuickCreate(false);
    }
  };

  const createScheduledMeeting = async () => {
    if (!scheduledMeeting.summary || !scheduledMeeting.date) {
      toast.error('Titre et date obligatoires');
      return;
    }

    try {
      const token = getToken();
      const startDateTime = `${scheduledMeeting.date}T${scheduledMeeting.start_time}:00`;
      const endTime = new Date(`${scheduledMeeting.date}T${scheduledMeeting.start_time}:00`);
      endTime.setMinutes(endTime.getMinutes() + parseInt(scheduledMeeting.duration));
      
      const attendees = scheduledMeeting.attendees.split(',').map(e => e.trim()).filter(e => e.includes('@'));

      const res = await fetch(`${API_URL}/api/google/calendar/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          summary: scheduledMeeting.summary,
          description: 'Visio AbracadaWorld Pro',
          start_datetime: startDateTime,
          end_datetime: endTime.toISOString(),
          attendees,
          add_meet: true
        })
      });

      if (res.ok) {
        const data = await res.json();
        setCreatedMeeting(data.event);
        setShowScheduleModal(false);
        setScheduledMeeting({ summary: '', date: '', start_time: '09:00', duration: '30', attendees: '' });
        loadRecentMeetings();
        toast.success('Visio planifiée !');
      } else {
        toast.error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyMeetLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Lien copié !');
  };

  const shareByEmail = (meeting) => {
    const subject = encodeURIComponent(`Invitation: ${meeting.summary}`);
    const body = encodeURIComponent(`Bonjour,\n\nJe vous invite à rejoindre cette visioconférence:\n\n${meeting.meet_link}\n\nCordialement`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  useEffect(() => {
    checkGoogleStatus();
  }, []);

  const formatDate = (dateString) => new Date(dateString).toLocaleString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  });

  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-8 text-center">
          {/* Badge Beta */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
            Version Beta
          </div>
          
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#1a2a4a] to-[#2d4a6a] flex items-center justify-center">
            <Video size={36} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Visio Pro</h1>
          <p className="text-gray-500 mb-6">Crée des réunions vidéo en un clic pour tes clients.</p>
          
          {/* Google Meet - Disponible */}
          <button
            onClick={connectGoogle}
            data-testid="connect-meet-pro-btn"
            className="w-full py-4 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors mb-3"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-6 h-6" />
            <span className="font-semibold text-gray-700">Google Meet</span>
            <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Disponible</span>
          </button>
          
          {/* Microsoft Teams - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 mb-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#5059C9"/>
              <path d="M12 2v20M2 7l10 5 10-5" stroke="#fff" strokeWidth="1"/>
            </svg>
            <span className="font-semibold text-gray-500">Microsoft Teams</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          {/* FaceTime - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="4" width="16" height="14" rx="2" fill="#34C759"/>
              <path d="M18 9l4-2v8l-4-2V9z" fill="#34C759"/>
            </svg>
            <span className="font-semibold text-gray-500">FaceTime</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          <p className="mt-6 text-xs text-gray-400">
            Tes données restent privées et sécurisées.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div 
        className="sticky top-0 z-20 px-4 py-4"
        style={{ background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)', paddingTop: 'max(16px, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Visioconférence</h1>
            <p className="text-white/70 text-sm">Google Meet</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Video size={22} className="text-white" />
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="p-4 space-y-4">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={createInstantMeeting}
          className="w-full p-6 bg-gradient-to-r from-green-500 to-teal-500 rounded-3xl text-white shadow-lg"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <Zap size={28} />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-bold">Visio instantanée</h3>
              <p className="text-white/80 text-sm">Créer un lien Meet maintenant</p>
            </div>
            <Plus size={24} />
          </div>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowScheduleModal(true)}
          className="w-full p-6 bg-white rounded-3xl shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#1a2a4a]/10 flex items-center justify-center">
              <Calendar size={28} className="text-[#1a2a4a]" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-bold text-gray-800">Planifier une visio</h3>
              <p className="text-gray-500 text-sm">Choisir date et inviter client</p>
            </div>
            <Plus size={24} className="text-gray-400" />
          </div>
        </motion.button>
      </div>

      {/* Réunions à venir */}
      <div className="p-4 pb-32">
        <h3 className="font-semibold text-gray-800 mb-3">Prochaines visios</h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : recentMeetings.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-2xl">
            <Video size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">Aucune visio planifiée</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentMeetings.map((meeting) => (
              <motion.div
                key={meeting.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-800">{meeting.summary}</h4>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <Clock size={14} />
                      {formatDate(meeting.start)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <a
                    href={meeting.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2.5 bg-green-500 text-white rounded-xl font-medium text-center hover:bg-green-600 flex items-center justify-center gap-2"
                  >
                    <Video size={16} />
                    Rejoindre
                  </a>
                  <button onClick={() => copyMeetLink(meeting.meet_link)} className="p-2.5 bg-gray-100 rounded-xl hover:bg-gray-200">
                    <Copy size={18} className="text-gray-600" />
                  </button>
                  <button onClick={() => shareByEmail(meeting)} className="p-2.5 bg-gray-100 rounded-xl hover:bg-gray-200">
                    <Mail size={18} className="text-gray-600" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal création */}
      <AnimatePresence>
        {(showQuickCreate || createdMeeting) && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={() => { setShowQuickCreate(false); setCreatedMeeting(null); }} />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-4 m-auto max-w-sm h-fit bg-white rounded-3xl z-50 p-6"
            >
              {!createdMeeting ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                  <p className="text-gray-600">Création...</p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                      <Check size={32} className="text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Visio créée !</h3>
                    <p className="text-gray-500 mt-1">{createdMeeting.summary}</p>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-4 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <Link2 size={16} />
                      <span className="font-medium">Lien de la visio</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="text" readOnly value={createdMeeting.meet_link} className="flex-1 text-sm bg-white border border-gray-200 rounded-lg px-3 py-2" />
                      <button onClick={() => copyMeetLink(createdMeeting.meet_link)} className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                        <Copy size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <a
                      href={createdMeeting.meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3.5 bg-green-500 text-white rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-green-600"
                    >
                      <Video size={20} />
                      Rejoindre
                    </a>
                    <button onClick={() => { setShowQuickCreate(false); setCreatedMeeting(null); }} className="w-full py-3.5 border-2 border-gray-200 text-gray-700 rounded-2xl font-medium hover:bg-gray-50">
                      Fermer
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal planifier - Version mobile plein écran / Desktop centré */}
      <AnimatePresence>
        {showScheduleModal && (
          isMobile ? (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-0 z-50 flex flex-col bg-white"
            >
              {/* Header */}
              <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
                <button onClick={() => setShowScheduleModal(false)} className="p-1 text-gray-500">
                  <ArrowLeft size={22} />
                </button>
                <h1 className="text-[15px] font-bold text-gray-900">Planifier une visio</h1>
              </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Titre *</label>
                    <input
                      type="text"
                      value={scheduledMeeting.summary}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, summary: e.target.value })}
                      placeholder="Visio avec client..."
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Date *</label>
                    <input
                      type="date"
                      value={scheduledMeeting.date}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, date: e.target.value })}
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Heure</label>
                      <input
                        type="time"
                        value={scheduledMeeting.start_time}
                        onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, start_time: e.target.value })}
                        className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Durée</label>
                      <select
                        value={scheduledMeeting.duration}
                        onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, duration: e.target.value })}
                        className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                      >
                        <option value="15">15 min</option>
                        <option value="30">30 min</option>
                        <option value="45">45 min</option>
                        <option value="60">1 heure</option>
                        <option value="90">1h30</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Email du client</label>
                    <input
                      type="text"
                      value={scheduledMeeting.attendees}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, attendees: e.target.value })}
                      placeholder="client@email.com"
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">Le client recevra une invitation</p>
                  </div>

                  <div className="p-4 bg-green-50 rounded-2xl flex items-center gap-3">
                    <Video size={24} className="text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Google Meet inclus</p>
                      <p className="text-sm text-green-600">Lien généré automatiquement</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={createScheduledMeeting}
                    disabled={!scheduledMeeting.summary || !scheduledMeeting.date}
                    className="flex-1 h-12 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold shadow-lg shadow-green-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Calendar size={18} />
                    Planifier
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowScheduleModal(false)} />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
              >
                <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-800">Planifier une visio</h2>
                    <button onClick={() => setShowScheduleModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                      <input
                        type="text"
                        value={scheduledMeeting.summary}
                        onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, summary: e.target.value })}
                        placeholder="Visio avec client..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <input
                        type="date"
                        value={scheduledMeeting.date}
                        onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, date: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Heure</label>
                        <input
                          type="time"
                          value={scheduledMeeting.start_time}
                          onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, start_time: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Durée</label>
                        <select
                          value={scheduledMeeting.duration}
                          onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, duration: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200"
                        >
                          <option value="15">15 min</option>
                          <option value="30">30 min</option>
                          <option value="45">45 min</option>
                          <option value="60">1 heure</option>
                          <option value="90">1h30</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email du client</label>
                      <input
                        type="text"
                        value={scheduledMeeting.attendees}
                        onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, attendees: e.target.value })}
                        placeholder="client@email.com"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200"
                      />
                      <p className="text-xs text-gray-500 mt-1">Le client recevra une invitation</p>
                    </div>

                    <div className="p-4 bg-green-50 rounded-2xl flex items-center gap-3">
                      <Video size={24} className="text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Google Meet inclus</p>
                        <p className="text-sm text-green-600">Lien généré automatiquement</p>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => setShowScheduleModal(false)}
                        className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                      <button
                        className="px-4 py-3 rounded-xl font-semibold text-white flex items-center gap-2"
                        style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                      >
                        <Sparkles size={16} />
                        Demander à Abra
                      </button>
                      <button
                        onClick={createScheduledMeeting}
                        className="flex-1 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 flex items-center justify-center gap-2"
                      >
                        <Calendar size={20} />
                        Planifier
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )
        )}
      </AnimatePresence>
    </div>
  );
}
